/*
 * What an active quiz keeps across the reload that turns session recording
 * off (review of #866, UX 2). Turning recording off has to reload the page (a
 * running recorder cannot be stopped), and an active quiz holds the student's
 * answers, marks, strikes, time per question and the block timer in memory
 * only until End Block. Before that reload the quiz page stashes them here for
 * its own address; the reloaded page takes them back once.
 *
 * Only that reload writes this: replay-consent.js runs the registered hooks in
 * reloadToStopRecording() and marks the reload; exam.jsx restores only when
 * takeRecordingReload() confirms this load is that reload. Session storage,
 * one tab, one minute.
 */
import { takeRecordingReload } from "./replay-consent";

export const EXAM_RELOAD_STATE_KEY = "qlearn_exam_reload_state";
const MAX_AGE_MS = 60 * 1000;

const addressKey = (href) => {
  try {
    const u = new URL(href, window.location.href);
    return `${u.pathname.replace(/\/+$/, "") || "/"}${u.search}`;
  } catch (e) {
    return null;
  }
};

const read = () => {
  try {
    const raw = window.sessionStorage.getItem(EXAM_RELOAD_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Merges one part (the page's answers, the footer's timer) into the stash for
// this address.
export const stashExamReloadState = (part, href = window.location.href) => {
  try {
    const address = addressKey(href);
    const current = read();
    const base = current && current.address === address ? current : { address };
    window.sessionStorage.setItem(EXAM_RELOAD_STATE_KEY, JSON.stringify({ ...base, ...part, address, at: Date.now() }));
  } catch (e) {
    // Blocked storage or unserializable state: the reload still happens.
  }
};

// The stash for this address, once; anything else (another address, older
// than a minute, unreadable) is dropped and gives null.
export const takeExamReloadState = (href = window.location.href) => {
  const stash = read();
  try { window.sessionStorage.removeItem(EXAM_RELOAD_STATE_KEY); } catch (e) { /* blocked */ }
  if (!stash || stash.address !== addressKey(href)) return null;
  if (!(Date.now() - Number(stash.at) < MAX_AGE_MS)) return null;
  return stash;
};

// Every quiz page load calls this once, on its first render: what to restore
// when this load is the stopping reload, else null. Saved state is cleared
// either way, so a stash whose reload never came back here (the mark did not
// match: another address, too late, a manual reload) does not outlive the
// next quiz page load.
export const takeQuizResume = (href = window.location.href) => {
  const resumed = takeRecordingReload(href);
  const kept = takeExamReloadState(href);
  return resumed ? (kept || {}) : null;
};
